import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CalendarDaysIcon,
  ChatBubbleLeftRightIcon,
  PhotoIcon,
  QrCodeIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { RepairRequest, RepairStatus, UserRole } from '../../types/firestore';
import { serviceProviderService } from '../../services/serviceProviderService';
import { getRepairRequestOperationalIndexState } from '../../shared/repairRequestDisplay';
import {
  getRepairRequestId,
  getRepairRequestUnreadState,
} from '../../utils/repairRequestReadModel';
import {
  getRepairRequestListHint,
  getRepairStatusMatrix,
  getRepairRequestStageLabel,
  getRepairRequestStatusLabel,
  getRepairRequestStatusVariant,
  requiresClientAction,
  requiresServiceAction,
  type RepairRequestWorkflowActor,
} from '../../utils/repairRequestWorkflow';
import {
  getRepairRequestDevicePresentation,
  getRepairRequestIssueSummary,
  getRepairRequestPresentation,
} from '../../utils/repairRequestPresentation';
import RepairRequestOrderCard from './RepairRequestOrderCard';
import {
  getRequestQueueCardShellClasses,
  type RequestQueueBucket,
} from '../../utils/requestQueueVisuals';
import {
  getRepairRequestFulfillmentListLabel,
  resolveRepairRequestFulfillmentSnapshot,
} from '../../shared/fulfillment';
import { Badge } from '../common/UIComponents';

interface RepairRequestCardProps {
  request: RepairRequest;
  currentUserRole: UserRole;
  currentUserId?: string;
  queueBucket?: RequestQueueBucket;
  serviceQueueAttentionLevel?: 'none' | 'static' | 'primary';
  serviceQueueAttentionKind?: 'needs_action' | 'fresh_request' | 'unread_messages';
  serviceDashboardMode?: boolean;
  serviceWorkshopMode?: boolean;
  compactListMode?: boolean;
  workspaceSimpleMode?: boolean;
  disableInlineExpand?: boolean;
  fullViewHref?: string;
  chatHref?: string;
  plannerHref?: string;
  onPlannerContextSelect?: () => void;
  isExpanded?: boolean;
  onCollapse?: () => void;
  onExpand?: () => void;
  showOperationalSignals?: boolean;
  isStale?: boolean;
}

const requestCardNavCtaBaseClass =
  'inline-flex min-h-[34px] shrink-0 items-center whitespace-nowrap rounded-md px-2.5 text-[13px] font-semibold transition-colors focus-outline [touch-action:manipulation]';

const requestCardNavIconClass = 'ml-1 h-4 w-4 opacity-80';
const requestCardPlannerCtaClass = 'rs-request-card-planner-link';
const queuePhotoButtonClass = 'rs-request-card-photo-link';
const workshopSecondaryButtonClass = 'rs-request-card-action rs-request-card-action--secondary';
const workshopPrimaryLinkClass = 'rs-request-card-action rs-request-card-action--primary';
const priorityRequestBadgeClass = 'uppercase tracking-[0.05em]';
const staleSignalBadgeClass = `${priorityRequestBadgeClass} text-status-warning-100`;

const serviceScheduleDateTimeFormatter = new Intl.DateTimeFormat('pl-PL', {
  timeZone: 'Europe/Warsaw',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

const clientPreferenceDateFormatter = new Intl.DateTimeFormat('pl-PL', {
  timeZone: 'Europe/Warsaw',
  day: 'numeric',
  month: 'short',
});

const toRequestDate = (value: unknown): Date | null => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value : null;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value);
    return Number.isFinite(date.getTime()) ? date : null;
  }

  if (typeof value === 'object') {
    const timestampLike = value as {
      toDate?: () => Date;
      toMillis?: () => number;
      seconds?: number;
      nanoseconds?: number;
      _seconds?: number;
      _nanoseconds?: number;
    };

    if (typeof timestampLike.toDate === 'function') {
      const date = timestampLike.toDate();
      return date instanceof Date && Number.isFinite(date.getTime()) ? date : null;
    }

    if (typeof timestampLike.toMillis === 'function') {
      const date = new Date(timestampLike.toMillis());
      return Number.isFinite(date.getTime()) ? date : null;
    }

    const seconds = timestampLike.seconds ?? timestampLike._seconds;
    const nanoseconds = timestampLike.nanoseconds ?? timestampLike._nanoseconds ?? 0;
    if (
      Number.isInteger(seconds) &&
      Number.isInteger(nanoseconds) &&
      (nanoseconds as number) >= 0 &&
      (nanoseconds as number) <= 999999999
    ) {
      const date = new Date((seconds as number) * 1000 + Math.floor((nanoseconds as number) / 1e6));
      return Number.isFinite(date.getTime()) ? date : null;
    }
  }

  return null;
};

const RequestCardNavigationCtas: React.FC<{
  detailHref: string;
  chatHref: string;
  chatLabel: string;
  highlightChatLabel: boolean;
  className?: string;
}> = ({ detailHref, chatHref, chatLabel, highlightChatLabel, className }) => {
  const rowClassName = `flex min-w-0 flex-wrap items-center justify-end gap-2${
    className ? ` ${className}` : ''
  }`;

  return (
    <div className={rowClassName} data-testid="repair-request-card-nav-ctas">
      <Link
        to={chatHref}
        className={`${requestCardNavCtaBaseClass} border border-transparent bg-transparent px-1.5 text-primary-light-300 hover:text-primary-light-100`}
      >
        <span className={highlightChatLabel ? 'text-ratuj-200' : undefined}>{chatLabel}</span>
        <ChatBubbleLeftRightIcon className={requestCardNavIconClass} />
      </Link>
      <Link
        to={detailHref}
        className={`${requestCardNavCtaBaseClass} border border-ratuj-300/45 bg-ratuj-300/16 text-primary-light-100 hover:bg-ratuj-300/22`}
      >
        Otwórz
        <ArrowRightIcon className={requestCardNavIconClass} />
      </Link>
    </div>
  );
};

type ClientQueueActionSummaryLabel = 'Twoja decyzja' | 'Aktualny krok' | 'Co teraz' | 'Zakończone';

const getClientQueueActionSummaryLabel = (
  request: RepairRequest
): ClientQueueActionSummaryLabel => {
  const statusMatrix = getRepairStatusMatrix(request.status);
  if (statusMatrix.isTerminal) {
    return 'Zakończone';
  }

  const presentation = getRepairRequestPresentation(request, 'client');
  if (presentation.primaryAction && !presentation.primaryAction.isDestructive) {
    return 'Twoja decyzja';
  }

  if (statusMatrix.nextActionOwner === 'service') {
    return 'Aktualny krok';
  }

  return 'Co teraz';
};

const SHORT_STATUS_LABELS: Partial<Record<RepairStatus, string>> = {
  [RepairStatus.AWAITING_FULFILLMENT]: 'Przekazanie',
  [RepairStatus.CANCELLED_BY_CLIENT]: 'Anulowane',
  [RepairStatus.CANCELLED_BY_SERVICE]: 'Anulowane',
  [RepairStatus.FINAL_QUOTE_ACCEPTED]: 'Wycena zaakceptowana',
  [RepairStatus.FINAL_QUOTE_REJECTED]: 'Odrzucone',
  [RepairStatus.PRELIMINARY_REJECTED]: 'Odrzucone',
  [RepairStatus.QUOTED]: 'Wycena gotowa',
};

const getQueueStatusLabel = (status: RepairStatus): string =>
  SHORT_STATUS_LABELS[status] || getRepairRequestStatusLabel(status);

const QR_VERSION = 3;
const QR_SIZE = QR_VERSION * 4 + 17;
const QR_DATA_CODEWORDS = 55;
const QR_ECC_CODEWORDS = 15;

const getQrFormatBits = (maskPattern: number): number => {
  const formatData = (1 << 3) | maskPattern; // L error correction, mask pattern 0.
  let bits = formatData << 10;
  const generator = 0b10100110111;
  for (let i = 14; i >= 10; i -= 1) {
    if (((bits >> i) & 1) !== 0) {
      bits ^= generator << (i - 10);
    }
  }
  return ((formatData << 10) | bits) ^ 0b101010000010010;
};

const multiplyQrField = (left: number, right: number): number => {
  let result = 0;
  let a = left;
  let b = right;
  while (b > 0) {
    if ((b & 1) !== 0) {
      result ^= a;
    }
    a <<= 1;
    if ((a & 0x100) !== 0) {
      a ^= 0x11d;
    }
    b >>= 1;
  }
  return result;
};

const powerQrField = (value: number, exponent: number): number => {
  let result = 1;
  for (let i = 0; i < exponent; i += 1) {
    result = multiplyQrField(result, value);
  }
  return result;
};

const buildQrGeneratorPolynomial = (degree: number): number[] => {
  let polynomial = [1];
  for (let i = 0; i < degree; i += 1) {
    const root = powerQrField(2, i);
    const next = new Array(polynomial.length + 1).fill(0);
    polynomial.forEach((coefficient, index) => {
      next[index] ^= multiplyQrField(coefficient, root);
      next[index + 1] ^= coefficient;
    });
    polynomial = next;
  }
  return polynomial;
};

const appendQrBits = (bits: number[], value: number, length: number): void => {
  for (let i = length - 1; i >= 0; i -= 1) {
    bits.push((value >> i) & 1);
  }
};

const encodeUtf8Bytes = (value: string): number[] => {
  const bytes: number[] = [];
  for (const character of value) {
    const codePoint = character.codePointAt(0) || 0;
    if (codePoint <= 0x7f) {
      bytes.push(codePoint);
    } else if (codePoint <= 0x7ff) {
      bytes.push(0xc0 | (codePoint >> 6), 0x80 | (codePoint & 0x3f));
    } else if (codePoint <= 0xffff) {
      bytes.push(
        0xe0 | (codePoint >> 12),
        0x80 | ((codePoint >> 6) & 0x3f),
        0x80 | (codePoint & 0x3f)
      );
    } else {
      bytes.push(
        0xf0 | (codePoint >> 18),
        0x80 | ((codePoint >> 12) & 0x3f),
        0x80 | ((codePoint >> 6) & 0x3f),
        0x80 | (codePoint & 0x3f)
      );
    }
  }
  return bytes;
};

const encodeQrPayload = (value: string): number[] => {
  const bytes = encodeUtf8Bytes(value);
  if (bytes.length > QR_DATA_CODEWORDS - 3) {
    throw new Error('QR route target is too long for the workshop card QR renderer.');
  }

  const bits: number[] = [];
  appendQrBits(bits, 0b0100, 4);
  appendQrBits(bits, bytes.length, 8);
  bytes.forEach((byte) => appendQrBits(bits, byte, 8));
  const capacityBits = QR_DATA_CODEWORDS * 8;
  appendQrBits(bits, 0, Math.min(4, capacityBits - bits.length));
  while (bits.length % 8 !== 0) {
    bits.push(0);
  }

  const data: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    data.push(bits.slice(i, i + 8).reduce((acc, bit) => (acc << 1) | bit, 0));
  }
  const pads = [0xec, 0x11];
  let padIndex = 0;
  while (data.length < QR_DATA_CODEWORDS) {
    data.push(pads[padIndex % pads.length]);
    padIndex += 1;
  }
  return data;
};

const buildQrErrorCorrection = (data: number[]): number[] => {
  const generator = buildQrGeneratorPolynomial(QR_ECC_CODEWORDS);
  const remainder = new Array(QR_ECC_CODEWORDS).fill(0);
  data.forEach((byte) => {
    const factor = byte ^ remainder.shift();
    remainder.push(0);
    for (let i = 0; i < QR_ECC_CODEWORDS; i += 1) {
      remainder[i] ^= multiplyQrField(generator[i], factor);
    }
  });
  return remainder;
};

const reserveQrModule = (
  modules: boolean[][],
  reserved: boolean[][],
  x: number,
  y: number,
  value: boolean
): void => {
  if (x < 0 || y < 0 || x >= QR_SIZE || y >= QR_SIZE) return;
  modules[y][x] = value;
  reserved[y][x] = true;
};

const drawQrFinder = (
  modules: boolean[][],
  reserved: boolean[][],
  startX: number,
  startY: number
): void => {
  for (let y = -1; y <= 7; y += 1) {
    for (let x = -1; x <= 7; x += 1) {
      const absoluteX = startX + x;
      const absoluteY = startY + y;
      const isFinder =
        x >= 0 &&
        y >= 0 &&
        x <= 6 &&
        y <= 6 &&
        (x === 0 || y === 0 || x === 6 || y === 6 || (x >= 2 && x <= 4 && y >= 2 && y <= 4));
      reserveQrModule(modules, reserved, absoluteX, absoluteY, isFinder);
    }
  }
};

const drawQrAlignment = (
  modules: boolean[][],
  reserved: boolean[][],
  centerX: number,
  centerY: number
): void => {
  for (let y = -2; y <= 2; y += 1) {
    for (let x = -2; x <= 2; x += 1) {
      const isDark = Math.max(Math.abs(x), Math.abs(y)) !== 1;
      reserveQrModule(modules, reserved, centerX + x, centerY + y, isDark);
    }
  }
};

const drawQrFunctionPatterns = (modules: boolean[][], reserved: boolean[][]): void => {
  drawQrFinder(modules, reserved, 0, 0);
  drawQrFinder(modules, reserved, QR_SIZE - 7, 0);
  drawQrFinder(modules, reserved, 0, QR_SIZE - 7);
  drawQrAlignment(modules, reserved, 22, 22);

  for (let i = 8; i < QR_SIZE - 8; i += 1) {
    reserveQrModule(modules, reserved, i, 6, i % 2 === 0);
    reserveQrModule(modules, reserved, 6, i, i % 2 === 0);
  }
  reserveQrModule(modules, reserved, 8, QR_SIZE - 8, true);

  for (let i = 0; i < 9; i += 1) {
    if (i !== 6) {
      reserveQrModule(modules, reserved, 8, i, false);
      reserveQrModule(modules, reserved, i, 8, false);
    }
  }
  for (let i = QR_SIZE - 8; i < QR_SIZE; i += 1) {
    reserveQrModule(modules, reserved, 8, i, false);
    reserveQrModule(modules, reserved, i, 8, false);
  }
};

const drawQrFormat = (modules: boolean[][], reserved: boolean[][]): void => {
  const bits = getQrFormatBits(0);
  const firstPositions = [
    [8, 0],
    [8, 1],
    [8, 2],
    [8, 3],
    [8, 4],
    [8, 5],
    [8, 7],
    [8, 8],
    [7, 8],
    [5, 8],
    [4, 8],
    [3, 8],
    [2, 8],
    [1, 8],
    [0, 8],
  ];
  const secondPositions = [
    [QR_SIZE - 1, 8],
    [QR_SIZE - 2, 8],
    [QR_SIZE - 3, 8],
    [QR_SIZE - 4, 8],
    [QR_SIZE - 5, 8],
    [QR_SIZE - 6, 8],
    [QR_SIZE - 7, 8],
    [8, QR_SIZE - 8],
    [8, QR_SIZE - 7],
    [8, QR_SIZE - 6],
    [8, QR_SIZE - 5],
    [8, QR_SIZE - 4],
    [8, QR_SIZE - 3],
    [8, QR_SIZE - 2],
    [8, QR_SIZE - 1],
  ];
  firstPositions.forEach(([x, y], index) =>
    reserveQrModule(modules, reserved, x, y, ((bits >> index) & 1) !== 0)
  );
  secondPositions.forEach(([x, y], index) =>
    reserveQrModule(modules, reserved, x, y, ((bits >> index) & 1) !== 0)
  );
};

const buildQrModules = (value: string): boolean[][] => {
  const modules = Array.from({ length: QR_SIZE }, () => Array(QR_SIZE).fill(false));
  const reserved = Array.from({ length: QR_SIZE }, () => Array(QR_SIZE).fill(false));
  drawQrFunctionPatterns(modules, reserved);

  const codewords = [...encodeQrPayload(value), ...buildQrErrorCorrection(encodeQrPayload(value))];
  const bits = codewords.flatMap((codeword) =>
    Array.from({ length: 8 }, (_, index) => (codeword >> (7 - index)) & 1)
  );
  let bitIndex = 0;
  let direction = -1;
  let y = QR_SIZE - 1;

  for (let rightX = QR_SIZE - 1; rightX >= 1; rightX -= 2) {
    if (rightX === 6) {
      rightX -= 1;
    }
    for (;;) {
      for (let column = 0; column < 2; column += 1) {
        const x = rightX - column;
        if (!reserved[y][x]) {
          const bit = bits[bitIndex] === 1;
          modules[y][x] = bit !== ((x + y) % 2 === 0);
          bitIndex += 1;
        }
      }
      y += direction;
      if (y < 0 || y >= QR_SIZE) {
        y -= direction;
        direction *= -1;
        break;
      }
    }
  }

  drawQrFormat(modules, reserved);
  return modules;
};

const WorkshopQrCode: React.FC<{ value: string; className?: string }> = ({ value, className }) => {
  const modules = useMemo(() => buildQrModules(value), [value]);
  const quietZone = 4;
  const size = QR_SIZE + quietZone * 2;

  return (
    <svg
      role="img"
      aria-label="Kod QR prowadzący do sprawy"
      viewBox={`0 0 ${size} ${size}`}
      className={className}
      shapeRendering="crispEdges"
      data-qr-value={value}
    >
      <rect width={size} height={size} fill="#f7faf7" rx="1.5" />
      {modules.flatMap((row, y) =>
        row.map((isDark, x) =>
          isDark ? (
            <rect
              key={`${x}-${y}`}
              x={x + quietZone}
              y={y + quietZone}
              width="1"
              height="1"
              fill="#0e1210"
            />
          ) : null
        )
      )}
    </svg>
  );
};

const RepairRequestCard: React.FC<RepairRequestCardProps> = ({
  request,
  currentUserRole,
  currentUserId,
  queueBucket,
  compactListMode = false,
  serviceDashboardMode = false,
  serviceWorkshopMode = false,
  workspaceSimpleMode = false,
  disableInlineExpand = false,
  chatHref,
  plannerHref,
  onPlannerContextSelect,
  isExpanded = false,
  onCollapse,
  onExpand,
  fullViewHref,
  showOperationalSignals = false,
  isStale = false,
  serviceQueueAttentionLevel = 'none',
  serviceQueueAttentionKind,
}) => {
  const [serviceName, setServiceName] = useState<string | null>(null);
  const [isLoadingServiceName, setIsLoadingServiceName] = useState<boolean>(false);
  const [photosModalOpen, setPhotosModalOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);
  const isClientFlowRole =
    currentUserRole === UserRole.CLIENT || currentUserRole === UserRole.ADMIN;
  const actor: RepairRequestWorkflowActor =
    currentUserRole === UserRole.SERVICE_ADMIN ? 'service' : 'client';
  const needsAction =
    actor === 'client'
      ? requiresClientAction(request.status)
      : requiresServiceAction(request.status);
  const stageLabel = getRepairRequestStageLabel(request.status);
  const listHint = getRepairRequestListHint(request.status, actor);
  const providerId = request.serviceProviderId || '';
  const requestId = getRepairRequestId(request);
  const operationalIndex = getRepairRequestOperationalIndexState(request);
  const devicePresentation = getRepairRequestDevicePresentation(request);
  const subjectLabel = devicePresentation.label;
  const subjectBrandModelLabel = devicePresentation.brandModelLabel;
  const photos = useMemo(
    () =>
      Array.isArray(request.photos)
        ? request.photos.filter((photo): photo is string => Boolean(photo))
        : [],
    [request.photos]
  );
  const detailHref = fullViewHref || `/app/request/${requestId || request.id || request.uid}`;
  const resolvedChatHref =
    chatHref || `${detailHref}${detailHref.includes('?') ? '&' : '?'}section=messages`;
  const shouldRenderQueueRow = compactListMode || disableInlineExpand || serviceDashboardMode;
  const serviceDashboardQueueMode = serviceDashboardMode && actor === 'service';
  const hasPriorityRequest = actor === 'service' && request.priorityRequest?.requested === true;
  const resolvedCurrentUserId =
    currentUserId ||
    (actor === 'service' ? request.serviceProviderId || '' : request.clientId || '');
  const fallbackCounterpartLabel =
    actor === 'service'
      ? `Właściciel zgłoszenia: ${
          request.clientDetails?.displayName ||
          request.clientDetails?.email ||
          request.clientId ||
          'Brak danych'
        }`
      : `Serwis: ${request.serviceProviderId || 'Brak danych'}`;
  const counterpartLabel =
    actor === 'client' && serviceName ? `Serwis: ${serviceName}` : fallbackCounterpartLabel;
  const queueCounterpartLabel =
    actor === 'service'
      ? request.clientDetails?.displayName ||
        request.clientDetails?.email ||
        request.clientId ||
        'Brak danych'
      : isLoadingServiceName
        ? 'Ładowanie serwisu...'
        : serviceName || request.serviceProviderId || 'Brak danych';

  useEffect(() => {
    if (!isClientFlowRole || !providerId) {
      return;
    }

    let isMounted = true;
    const cached = serviceProviderService.getCachedServiceProviderProfile(providerId);
    const cachedName =
      typeof cached?.serviceName === 'string' && cached.serviceName.trim()
        ? cached.serviceName.trim()
        : null;
    if (cachedName) {
      setServiceName(cachedName);
    } else {
      setIsLoadingServiceName(true);
    }

    serviceProviderService
      .getServiceProviderProfile(providerId)
      .then((profile) => {
        if (!isMounted) return;
        const fetchedName =
          typeof profile?.serviceName === 'string' && profile.serviceName.trim()
            ? profile.serviceName.trim()
            : 'Nieznany serwis';
        setServiceName(fetchedName);
      })
      .catch((error) => {
        console.error('Error fetching service provider name:', error);
        if (!isMounted) return;
        setServiceName(cachedName || 'Nieznany serwis');
      })
      .finally(() => {
        if (!isMounted) return;
        setIsLoadingServiceName(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isClientFlowRole, providerId]);

  const formatDate = (timestamp: any): string => {
    if (!timestamp || typeof timestamp.toDate !== 'function') return 'Brak danych';
    try {
      return timestamp.toDate().toLocaleDateString('pl-PL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return 'Brak danych';
    }
  };

  const statusMatrix = getRepairStatusMatrix(request.status);
  const statusVariant = getRepairRequestStatusVariant(request.status);
  const statusToneClass =
    statusVariant === 'error'
      ? 'border-status-error-500/35 bg-status-error-500/10 text-status-error-500'
      : statusVariant === 'warning'
        ? 'border-status-warning-500/35 bg-status-warning-500/10 text-status-warning-500'
        : statusVariant === 'success'
          ? 'border-status-success-500/35 bg-status-success-500/10 text-status-success-500'
          : 'border-primary-dark-300/45 bg-primary-dark-200/35 text-primary-light-200';
  if (!shouldRenderQueueRow && requestId) {
    return (
      <RepairRequestOrderCard
        requestId={requestId}
        request={request}
        actor={actor}
        currentUserId={resolvedCurrentUserId}
        counterpartLabel={counterpartLabel}
        showReview={false}
        existingReview={null}
        reviewSubmittedSuccessfully={false}
        onReviewAdded={() => undefined}
        variant="expanded"
        loadingCounterpartLabel={isLoadingServiceName}
        compactListMode={compactListMode}
        disableInlineExpand={disableInlineExpand}
        isExpanded={Boolean(isExpanded)}
        onExpand={onExpand}
        onBack={isExpanded ? onCollapse : undefined}
        backLabel={isExpanded ? 'Zwiń kartę' : 'Wróć'}
        fullViewHref={detailHref}
        workspaceSimpleMode={workspaceSimpleMode}
      />
    );
  }

  const queueShellClass = getRequestQueueCardShellClasses(queueBucket);
  const issueSummary = getRepairRequestIssueSummary(request, 120);
  const fulfillment = resolveRepairRequestFulfillmentSnapshot(request);
  const fulfillmentListLabel = getRepairRequestFulfillmentListLabel(fulfillment);
  const scheduledServiceStartAt =
    request.serviceSchedule?.status === 'scheduled'
      ? toRequestDate(request.serviceSchedule.scheduledStartAt)
      : null;
  const clientPreferenceDate = toRequestDate(request.scheduledDate);
  const hasActiveServicePlan = Boolean(scheduledServiceStartAt);
  const plannerCtaLabel = hasActiveServicePlan ? 'Przełóż' : 'Zaplanuj';
  const shouldShowPlannerCta = Boolean(
    plannerHref && serviceDashboardQueueMode && !statusMatrix.isTerminal
  );
  const planningMetaItems = serviceDashboardQueueMode
    ? [
        scheduledServiceStartAt
          ? {
              label: 'Termin pracy',
              value: serviceScheduleDateTimeFormatter.format(scheduledServiceStartAt),
              quiet: false,
            }
          : null,
        clientPreferenceDate
          ? {
              label: 'Preferencja klienta',
              value: clientPreferenceDateFormatter.format(clientPreferenceDate),
              quiet: Boolean(scheduledServiceStartAt),
            }
          : null,
      ].filter((item): item is { label: string; value: string; quiet: boolean } => item !== null)
    : [];
  const unreadState = getRepairRequestUnreadState(request, actor);
  const hasUnreadChatMessages = unreadState.hasUnreadMessages;
  const hasFreshRequestActivity = actor === 'service' && unreadState.hasFreshRequestActivity;
  const shouldHighlightSecondaryCta = hasUnreadChatMessages || hasFreshRequestActivity;
  const secondaryCtaHref = hasFreshRequestActivity ? detailHref : resolvedChatHref;
  const chatStatusLabel = hasUnreadChatMessages
    ? 'Nowe wiadomości'
    : hasFreshRequestActivity
      ? 'Nowe zgłoszenie'
      : actor === 'client'
        ? 'Chat'
        : 'Brak nowych';
  const metaParts = [
    subjectBrandModelLabel || null,
    queueCounterpartLabel,
    actor === 'service' ? operationalIndex.value || 'bez indeksu' : formatDate(request.createdAt),
  ].filter(Boolean);
  const actionSummaryLabel =
    actor === 'service' ? 'Następny krok' : getClientQueueActionSummaryLabel(request);
  const contextPreview =
    actor === 'service' && fulfillmentListLabel
      ? { label: 'Realizacja', value: fulfillmentListLabel }
      : actor === 'client'
        ? { label: 'Problem', value: issueSummary }
        : fulfillmentListLabel
          ? { label: 'Realizacja', value: fulfillmentListLabel }
          : null;
  const decisionTextClass =
    needsAction && actor === 'service'
      ? 'text-status-warning-400'
      : needsAction
        ? 'text-primary-light-100'
        : 'text-primary-light-200';
  const workshopIndexLabel = operationalIndex.value || 'bez indeksu';
  const workshopTitle = subjectBrandModelLabel
    ? `${subjectLabel} / ${subjectBrandModelLabel}`
    : subjectLabel;
  const workshopQrTarget = `/app/request/${requestId || request.id || request.uid}`;
  const hasServiceQueueAttention =
    serviceDashboardQueueMode &&
    serviceQueueAttentionLevel !== 'none' &&
    Boolean(serviceQueueAttentionKind);
  const serviceQueueAttentionClassName = hasServiceQueueAttention
    ? [
        'rs-request-queue-card--service-attention',
        `rs-request-queue-card--service-attention-${serviceQueueAttentionKind}`,
        serviceQueueAttentionLevel === 'primary'
          ? 'rs-request-queue-card--service-attention-primary'
          : 'rs-request-queue-card--service-attention-static',
      ].join(' ')
    : '';
  const shouldCuePrimaryEntry =
    hasServiceQueueAttention &&
    serviceQueueAttentionLevel === 'primary' &&
    (serviceQueueAttentionKind === 'fresh_request' ||
      serviceQueueAttentionKind === 'unread_messages');
  const renderPlannerCta = () => {
    if (!shouldShowPlannerCta || !plannerHref) {
      return null;
    }

    const content = (
      <>
        <CalendarDaysIcon className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden="true" />
        <span className="truncate">{plannerCtaLabel}</span>
      </>
    );

    if (onPlannerContextSelect) {
      return (
        <button
          type="button"
          className={requestCardPlannerCtaClass}
          onClick={onPlannerContextSelect}
        >
          {content}
        </button>
      );
    }

    return (
      <Link to={plannerHref} className={requestCardPlannerCtaClass}>
        {content}
      </Link>
    );
  };

  if (serviceDashboardQueueMode && serviceWorkshopMode) {
    return (
      <>
        <article
          data-testid="service-workshop-request-card"
          data-queue-bucket={queueBucket || 'neutral'}
          data-service-attention={hasServiceQueueAttention ? serviceQueueAttentionKind : undefined}
          data-service-attention-level={
            hasServiceQueueAttention ? serviceQueueAttentionLevel : undefined
          }
          className={`rs-request-queue-card rs-request-queue-card--workshop flex min-w-0 flex-col rounded-lg border ${queueShellClass} ${serviceQueueAttentionClassName} p-3 pl-5 sm:p-3.5 sm:pl-6`}
        >
          <div className="flex min-w-0 items-start justify-between gap-2.5">
            <div className="min-w-0 flex-1">
              <p className="line-clamp-2 break-words text-[15px] font-semibold uppercase leading-tight tracking-[0.025em] text-primary-light-100 sm:text-base">
                {workshopTitle}
              </p>
            </div>
            <span
              className={`rs-request-status-pill ${statusToneClass}`}
              title={getQueueStatusLabel(request.status)}
            >
              {getQueueStatusLabel(request.status)}
            </span>
          </div>

          <div
            className="mt-3 grid min-w-0 grid-cols-3 gap-2"
            data-testid="service-workshop-actions"
          >
            <button
              type="button"
              disabled={photos.length === 0}
              aria-label={
                photos.length === 0
                  ? 'Brak zdjęć w zleceniu'
                  : `Otwórz zdjęcia zlecenia (${photos.length})`
              }
              onClick={() => {
                setActivePhotoIndex(0);
                setPhotosModalOpen(true);
              }}
              className={workshopSecondaryButtonClass}
            >
              <span className="flex min-w-0 flex-col items-center gap-1">
                <PhotoIcon className="h-4 w-4" />
                <span className="truncate">FOTO</span>
              </span>
            </button>
            <button
              type="button"
              aria-label={`Pokaż QR CODE dla ${workshopIndexLabel}`}
              onClick={() => setQrModalOpen(true)}
              className={workshopSecondaryButtonClass}
            >
              <span className="flex min-w-0 flex-col items-center gap-1">
                <QrCodeIcon className="h-4 w-4" />
                <span className="truncate">QR CODE</span>
              </span>
            </button>
            <Link
              to={detailHref}
              className={`${workshopPrimaryLinkClass} ${
                shouldCuePrimaryEntry ? 'rs-service-entry-attention' : ''
              }`}
              aria-label="Wejdź do sprawy"
            >
              <span className="truncate">WEJDŹ →</span>
            </Link>
          </div>

          <p className="mt-3 break-all font-mono text-2xl font-semibold leading-none tracking-[0.04em] text-primary-light-100 sm:text-[28px]">
            {workshopIndexLabel}
          </p>
          <p className="mt-2 line-clamp-2 text-sm leading-snug text-primary-light-300">
            {listHint}
          </p>

          {planningMetaItems.length > 0 ? (
            <div
              className="mt-2 space-y-0.5 text-[12px] leading-snug"
              data-testid="service-planning-meta"
            >
              {planningMetaItems.map((item) => (
                <p
                  key={item.label}
                  className={`min-w-0 truncate ${
                    item.quiet ? 'text-primary-light-500' : 'text-primary-light-300'
                  }`}
                >
                  <span className="text-primary-light-500">{item.label}: </span>
                  {item.value}
                </p>
              ))}
            </div>
          ) : null}

          <div className="mt-3 flex min-w-0 flex-wrap items-center justify-between gap-2 border-t border-primary-dark-300/20 pt-2">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              {hasPriorityRequest ? (
                <Badge variant="warning" size="sm" className={priorityRequestBadgeClass}>
                  Pilne
                </Badge>
              ) : null}
              {showOperationalSignals && isStale ? (
                <Badge
                  variant="warning"
                  size="sm"
                  className={staleSignalBadgeClass}
                  aria-label="Sygnał stale-active 48H"
                >
                  48H
                </Badge>
              ) : null}
              {photos.length > 0 ? (
                <span className="text-[12px] text-primary-light-400">
                  {photos.length === 1 ? '1 zdjęcie' : `${photos.length} zdjęć`}
                </span>
              ) : null}
              {renderPlannerCta()}
            </div>
            <Link
              to={secondaryCtaHref}
              className="inline-flex min-h-[30px] min-w-0 items-center gap-1 rounded-md px-1.5 text-[13px] font-medium text-primary-light-300 transition-colors hover:text-primary-light-100 focus-outline"
            >
              <span className={shouldHighlightSecondaryCta ? 'text-ratuj-200' : undefined}>
                {chatStatusLabel}
              </span>
              <ChatBubbleLeftRightIcon className="h-4 w-4 opacity-80" />
            </Link>
          </div>
        </article>

        {qrModalOpen ? (
          <div
            role="dialog"
            aria-label="Kod QR zlecenia"
            className="fixed inset-0 z-[100] flex items-center justify-center bg-primary-dark-100/88 p-4"
          >
            <div className="w-full max-w-sm rounded-xl border border-primary-dark-300/75 bg-primary-dark-200/95 p-4 shadow-card">
              <div className="mb-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-primary-light-100">QR CODE</p>
                  <p className="mt-0.5 break-all font-mono text-xs text-primary-light-400">
                    {workshopIndexLabel}
                  </p>
                </div>
                <button
                  type="button"
                  aria-label="Zamknij kod QR"
                  onClick={() => setQrModalOpen(false)}
                  className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-primary-dark-300/65 text-primary-light-200 hover:bg-primary-dark-300/40"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
              <div className="mx-auto w-full max-w-[260px] rounded-xl bg-primary-light-100 p-3">
                <WorkshopQrCode value={workshopQrTarget} className="h-auto w-full" />
              </div>
              <p className="mt-3 break-all text-center text-xs text-primary-light-400">
                {workshopQrTarget}
              </p>
            </div>
          </div>
        ) : null}

        {photosModalOpen && photos.length > 0 ? (
          <div
            role="dialog"
            aria-label="Podgląd zdjęć zlecenia"
            className="fixed inset-0 z-[100] flex items-center justify-center bg-primary-dark-100/85 p-4"
          >
            <div className="w-full max-w-3xl rounded-xl border border-primary-dark-300/75 bg-primary-dark-200/95 p-3">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-sm font-semibold text-primary-light-100">
                  Zdjęcie {activePhotoIndex + 1} / {photos.length}
                </p>
                <button
                  type="button"
                  aria-label="Zamknij podgląd zdjęć"
                  onClick={() => setPhotosModalOpen(false)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-primary-dark-300/65 text-primary-light-200 hover:bg-primary-dark-300/40"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
              <img
                src={photos[activePhotoIndex]}
                alt={`Zdjęcie ${activePhotoIndex + 1}`}
                className="max-h-[65vh] w-full rounded-lg object-contain"
              />
              {photos.length > 1 ? (
                <div className="mt-3 flex items-center justify-between gap-2">
                  <button
                    type="button"
                    aria-label="Poprzednie zdjęcie"
                    onClick={() =>
                      setActivePhotoIndex((prev) => (prev - 1 + photos.length) % photos.length)
                    }
                    className="inline-flex min-h-[36px] items-center rounded-md border border-primary-dark-300/70 px-3 py-1.5 text-xs text-primary-light-200 hover:bg-primary-dark-300/40"
                  >
                    <ArrowLeftIcon className="mr-1 h-4 w-4" />
                    Poprzednie
                  </button>
                  <button
                    type="button"
                    aria-label="Następne zdjęcie"
                    onClick={() => setActivePhotoIndex((prev) => (prev + 1) % photos.length)}
                    className="inline-flex min-h-[36px] items-center rounded-md border border-primary-dark-300/70 px-3 py-1.5 text-xs text-primary-light-200 hover:bg-primary-dark-300/40"
                  >
                    Następne
                    <ArrowRightIcon className="ml-1 h-4 w-4" />
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </>
    );
  }

  return (
    <>
      <article
        data-queue-bucket={queueBucket || 'neutral'}
        data-service-attention={hasServiceQueueAttention ? serviceQueueAttentionKind : undefined}
        data-service-attention-level={
          hasServiceQueueAttention ? serviceQueueAttentionLevel : undefined
        }
        className={`rs-request-queue-card flex min-h-[128px] min-w-0 flex-col rounded-lg border ${queueShellClass} ${serviceQueueAttentionClassName} p-2.5 pl-3.5 sm:min-h-[132px] sm:p-3 sm:pl-4`}
      >
        <div className="flex min-w-0 items-start justify-between gap-2">
          <div className="min-w-0 flex-1 pr-1">
            <p className="line-clamp-2 break-words text-[15px] font-semibold leading-snug text-primary-light-100">
              {actor === 'client' && subjectBrandModelLabel
                ? `${subjectLabel} · ${subjectBrandModelLabel}`
                : subjectLabel}
            </p>
          </div>
          <div className="flex min-w-0 flex-shrink-0 flex-wrap items-center justify-end gap-1.5">
            <span
              data-testid="repair-request-status-pill"
              className={`rs-request-status-pill ${statusToneClass}`}
            >
              {getQueueStatusLabel(request.status)}
            </span>
          </div>
        </div>

        <p className="mt-1 line-clamp-2 min-w-0 break-words text-[11px] leading-snug text-primary-light-400">
          {actor === 'client'
            ? `${queueCounterpartLabel} · ${formatDate(request.updatedAt || request.createdAt)}`
            : metaParts.join(' · ')}
        </p>

        <div
          className="mt-2 grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-baseline gap-x-2 gap-y-0.5"
          aria-label={
            actor === 'service' ? `Następny krok. Etap: ${stageLabel}. ${listHint}` : undefined
          }
        >
          <p className="whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.06em] text-primary-light-400">
            {actionSummaryLabel}
          </p>
          <p
            className={`line-clamp-2 min-w-0 text-sm font-medium leading-snug ${decisionTextClass}`}
          >
            {listHint}
          </p>
        </div>

        {contextPreview ? (
          <p className="mt-1.5 line-clamp-2 min-w-0 break-words text-xs leading-relaxed text-primary-light-300">
            <span className="text-primary-light-400">{contextPreview.label}: </span>
            {contextPreview.value}
          </p>
        ) : null}

        {planningMetaItems.length > 0 ? (
          <div
            className="mt-1.5 space-y-0.5 text-[11px] leading-snug"
            data-testid="service-planning-meta"
          >
            {planningMetaItems.map((item) => (
              <p
                key={item.label}
                className={`min-w-0 truncate ${
                  item.quiet ? 'text-primary-light-500' : 'text-primary-light-300'
                }`}
              >
                <span className="text-primary-light-500">{item.label}: </span>
                {item.value}
              </p>
            ))}
          </div>
        ) : null}

        <div className="mt-auto flex w-full flex-wrap items-center justify-between gap-2 pt-2">
          <div
            className="flex min-w-0 flex-wrap items-center gap-2 text-[11px] text-primary-light-400"
            data-testid={serviceDashboardQueueMode ? 'service-dashboard-cta-left' : undefined}
          >
            {hasPriorityRequest ? (
              <Badge variant="warning" size="sm" className={priorityRequestBadgeClass}>
                Pilne
              </Badge>
            ) : null}
            {actor === 'client' ? (
              <span data-testid="client-request-photo-count">Zdjęcia: {photos.length}</span>
            ) : (
              <button
                type="button"
                disabled={photos.length === 0}
                aria-label={
                  photos.length === 0
                    ? 'Brak zdjęć w zleceniu'
                    : `Otwórz zdjęcia zlecenia (${photos.length})`
                }
                onClick={() => {
                  setActivePhotoIndex(0);
                  setPhotosModalOpen(true);
                }}
                className={queuePhotoButtonClass}
              >
                <PhotoIcon className="h-3.5 w-3.5" />
                <span>{photos.length === 1 ? '1 zdjęcie' : `${photos.length} zdjęć`}</span>
              </button>
            )}
            {showOperationalSignals && actor === 'service' && isStale ? (
              <Badge
                variant="warning"
                size="sm"
                className={staleSignalBadgeClass}
                aria-label="Sygnał stale-active 48H"
              >
                48H
              </Badge>
            ) : null}
            {renderPlannerCta()}
          </div>
          <RequestCardNavigationCtas
            detailHref={detailHref}
            chatHref={secondaryCtaHref}
            chatLabel={chatStatusLabel}
            highlightChatLabel={shouldHighlightSecondaryCta}
          />
        </div>
      </article>

      {photosModalOpen && photos.length > 0 ? (
        <div
          role="dialog"
          aria-label="Podgląd zdjęć zlecenia"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-primary-dark-100/85 p-4"
        >
          <div className="w-full max-w-3xl rounded-xl border border-primary-dark-300/75 bg-primary-dark-200/95 p-3">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-sm font-semibold text-primary-light-100">
                Zdjęcie {activePhotoIndex + 1} / {photos.length}
              </p>
              <button
                type="button"
                aria-label="Zamknij podgląd zdjęć"
                onClick={() => setPhotosModalOpen(false)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-primary-dark-300/65 text-primary-light-200 hover:bg-primary-dark-300/40"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
            <img
              src={photos[activePhotoIndex]}
              alt={`Zdjęcie ${activePhotoIndex + 1}`}
              className="max-h-[65vh] w-full rounded-lg object-contain"
            />
            {photos.length > 1 ? (
              <div className="mt-3 flex items-center justify-between">
                <button
                  type="button"
                  aria-label="Poprzednie zdjęcie"
                  onClick={() =>
                    setActivePhotoIndex((prev) => (prev - 1 + photos.length) % photos.length)
                  }
                  className="inline-flex items-center rounded-md border border-primary-dark-300/70 px-3 py-1.5 text-xs text-primary-light-200 hover:bg-primary-dark-300/40"
                >
                  <ArrowLeftIcon className="mr-1 h-4 w-4" />
                  Poprzednie zdjęcie
                </button>
                <button
                  type="button"
                  aria-label="Następne zdjęcie"
                  onClick={() => setActivePhotoIndex((prev) => (prev + 1) % photos.length)}
                  className="inline-flex items-center rounded-md border border-primary-dark-300/70 px-3 py-1.5 text-xs text-primary-light-200 hover:bg-primary-dark-300/40"
                >
                  Następne zdjęcie
                  <ArrowRightIcon className="ml-1 h-4 w-4" />
                </button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
};

export default RepairRequestCard;
